import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useStaticQuery, graphql, navigate } from 'gatsby';
import { Link, Router, useLocation } from '@reach/router';
import { useQuery } from '@apollo/client';

import PrivateRoute from './private-route';
import Wrapper from '../elements/wrapper';
import { media, TransitionMixin } from '../helpers';
import Orders from './orders';
import Wishlist from './wishlist';

import FooterBlogPost from './footer-blog-post';

import { USER_QUERY } from './mutations';

const sidebarLinks = [
    {
        title: 'Order History',
        url: '/accounts/dashboard',
    },
    {
        title: 'My Wishlist',
        url: '/accounts/dashboard/wishlist',
    },
    {
        title: 'Saved Color Palettes',
        url: '/orders',
    },
];

const DashboardContainer = styled.div`
    padding: 50px 0 0;

    > .title-container {
        border-bottom: 2px solid #f74d33;
        margin-bottom: 20px;
        ${media.medium`max-width: 82vw;`}

        h1 {
            font-size: 2.5rem;
            margin: 0;
            color: #f74d33;
            font-weight: 800;
            font-family: neue-kabel, sans-serif;
            padding-bottom: 15px;
        }
    }
    .account-wrapper {
        padding-bottom: 70px;
        ${media.medium`max-width: 82vw; display: flex;`}

        .sidebar-container {
            flex: 1;
            border-right: 2px solid #f74d33;
            padding-right: 20px;
            .inner-wrap {
                .user-info {
                    margin-bottom: 50px;
                    p {
                        margin: 0;
                        font-size: 0.9rem;
                        color: #0f1212;

                        &.name {
                            font-weight: bold;
                            font-size: 1.8rem;
                            line-height: 1.5;
                            font-weight: 800;
                            font-family: neue-kabel, sans-serif;
                        }
                        &.email {
                            margin-bottom: 30px;
                        }
                        &.reset {
                            a {
                                color: #702716;
                            }
                        }
                    }
                }

                .accounts-nav {
                    nav {
                        ul {
                            padding: 0;
                            margin: 0;
                            list-style: none;
                            li {
                                margin-bottom: 15px;
                                a {
                                    font-family: neue-kabel, sans-serif;
                                    font-weight: 800;
                                    color: #000;
                                    text-decoration: none;
                                    font-size: 1.7rem;
                                    line-height: 1.3;
                                    opacity: 0.3;
                                    color: #f74d33;
                                    ${TransitionMixin('.25s')}

                                    &:hover, &.active {
                                        opacity: 1;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        .primary-container {
            flex: 3;
            padding-left: 20px;
        }
    }

    .footer-post-container {
        z-index: 100;
        position: relative;
        bottom: -5vh;
        margin-top: -5vh;
    }
`;

function logout() {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('cid');
    }
    navigate('/');
}

const Dashboard = () => {
    const postData = useStaticQuery(graphql`
        query {
            allWpHeadlessOption(filter: { title: { eq: "Account Pages" } }) {
                edges {
                    node {
                        id
                        account_pages_acf {
                            designBoardArticle {
                                articleTitle
                                articleLink {
                                    title
                                    url
                                }
                                articleImage {
                                    localFile {
                                        childImageSharp {
                                            fluid(maxWidth: 1000) {
                                                ...GatsbyImageSharpFluid
                                            }
                                        }
                                    }
                                }
                            }
                            orderHistoryArticle {
                                articleLink {
                                    title
                                    url
                                }
                                articleTitle
                                articleImage {
                                    localFile {
                                        childImageSharp {
                                            fluid(maxWidth: 1000) {
                                                ...GatsbyImageSharpFluid
                                            }
                                        }
                                    }
                                }
                            }
                            savedPalettesArticle {
                                articleTitle
                                articleLink {
                                    title
                                    url
                                }
                                articleImage {
                                    localFile {
                                        childImageSharp {
                                            fluid(maxWidth: 1000) {
                                                ...GatsbyImageSharpFluid
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const location = useLocation();

    const [currentPost, setCurrentPost] = useState({});
    // get customer info
    let token;

    if (typeof window !== 'undefined') {
        token = JSON.parse(localStorage.getItem('cid')).token;
    }

    const { loading, error, data } = useQuery(USER_QUERY, {
        variables: { customerAccessToken: token },
    });

    // set to user on load
    const [name, setName] = useState();
    const [email, setEmail] = useState();
    useEffect(() => {
        if (!loading) {
            const joinedName =
                data.customer.firstName + ' ' + data.customer.lastName;

            setName(joinedName);
            setEmail(data.customer.email);
        }
    }, [loading]);

    const isActive = ({ isCurrent }) => {
        return isCurrent ? { className: 'active' } : {};
    };

    useEffect(() => {
        let dashboardPost =
            postData?.allWpHeadlessOption.edges[0].node.account_pages_acf
                .orderHistoryArticle;
        let designBoardPost =
            postData?.allWpHeadlessOption.edges[0].node.account_pages_acf
                .designBoardArticle;
        let palettesPost =
            postData?.allWpHeadlessOption.edges[0].node.account_pages_acf
                .savedPalettesArticle;
        if (location.pathname.includes('wishlist')) {
            setCurrentPost(designBoardPost);
        } else {
            setCurrentPost(dashboardPost);
        }
        // if
    }, [location]);

    return (
        <DashboardContainer>
            <Wrapper className="title-container">
                <h1>Welcome back!</h1>
            </Wrapper>
            <Wrapper className="account-wrapper">
                <div className="sidebar-container">
                    <div className="inner-wrap">
                        <div className="user-info">
                            {/* basic info */}
                            <p className="name">{name}</p>
                            <p className="email">{email}</p>
                            <p className="reset">
                                <Link to="/dashboard/reset">
                                    Reset Password
                                </Link>
                            </p>
                        </div>
                        <div className="accounts-nav">
                            <nav>
                                <ul>
                                    {sidebarLinks &&
                                        sidebarLinks.map((link, index) => {
                                            return (
                                                <li key={index}>
                                                    <Link
                                                        getProps={isActive}
                                                        to={link.url}
                                                    >
                                                        {link.title}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    <li>
                                        <button onClick={() => logout()}>
                                            Logout
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
                <div className="primary-container">
                    <div className="inner-wrap">
                        <Router basepath="/accounts/dashboard">
                            <PrivateRoute path="/" component={Orders} />
                            <PrivateRoute
                                path="/wishlist"
                                component={Wishlist}
                            />
                        </Router>
                    </div>
                </div>
            </Wrapper>
            <div className="footer-post-container">
                {currentPost && <FooterBlogPost post={currentPost} />}
            </div>
        </DashboardContainer>
    );
};

export default Dashboard;
